import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Checkbox } from '../../../components/ui/checkbox';
import { Separator } from '../../../components/ui/separator';
import { Alert } from '../../../components/ui/alert';

interface RaynetUser {
  id: number;
  raynet_id: number;
  username: string;
  full_name: string;
  person_id?: number;
  email?: string;
  user_role_name?: string;
  is_active: boolean;
  is_available_for_forms: boolean;
  is_admin: boolean;
  last_sync_at: string;
  created_at: string;
  updated_at: string;
}

interface RaynetUsersStats {
  total: number;
  active: number;
  available: number;
  lastSync: string | null;
}

export function RaynetUsersTab() {
  const [users, setUsers] = useState<RaynetUser[]>([]);
  const [stats, setStats] = useState<RaynetUsersStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Načtení uživatelů při mount
  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/admin/raynet-users');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data);
      } else {
        throw new Error(data.error || 'Neočekávaná chyba');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err instanceof Error ? err.message : 'Chyba při načítání uživatelů');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/raynet-users/stats');
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      
      const response = await fetch('/api/admin/raynet-users/sync', {
        method: 'POST',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(data.message);
        await fetchUsers();
        await fetchStats();
      } else {
        throw new Error(data.error || 'Chyba při synchronizaci');
      }
    } catch (err) {
      console.error('Error syncing users:', err);
      setError(err instanceof Error ? err.message : 'Chyba při synchronizaci uživatelů');
    } finally {
      setSyncing(false);
    }
  };

  const handleToggleAvailability = async (raynetId: number, isAvailable: boolean) => {
    try {
      setError(null);
      
      const response = await fetch(`/api/admin/raynet-users/${raynetId}/toggle`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isAvailable }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Aktualizuj lokální stav
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.raynet_id === raynetId
              ? { ...user, is_available_for_forms: isAvailable }
              : user
          )
        );
        await fetchStats();
        setSuccess(data.message);
      } else {
        throw new Error(data.error || 'Chyba při změně dostupnosti');
      }
    } catch (err) {
      console.error('Error toggling availability:', err);
      setError(err instanceof Error ? err.message : 'Chyba při změně dostupnosti uživatele');
    }
  };

  const handleToggleAdmin = async (raynetId: number, isAdmin: boolean) => {
    try {
      setError(null);
      
      const response = await fetch(`/api/admin/raynet-users/${raynetId}/toggle-admin`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isAdmin }),
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Aktualizuj lokální stav
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.raynet_id === raynetId
              ? { ...user, is_admin: isAdmin }
              : user
          )
        );
        setSuccess(data.message);
      } else {
        throw new Error(data.error || 'Chyba při změně admin oprávnění');
      }
    } catch (err) {
      console.error('Error toggling admin:', err);
      setError(err instanceof Error ? err.message : 'Chyba při změně admin oprávnění uživatele');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('cs-CZ');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Správa obchodníků</h2>
          <p className="text-gray-600">
            Spravujte, kteří uživatelé z Raynetu mohou být vybráni jako obchodníci ve formuláři.
          </p>
        </div>
        <Button 
          onClick={handleSync} 
          disabled={syncing}
          className="bg-orange-600 hover:bg-orange-700"
        >
          {syncing ? 'Synchronizuji...' : 'Synchronizovat s Raynet'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <p>{error}</p>
        </Alert>
      )}

      {success && (
        <Alert>
          <p>{success}</p>
        </Alert>
      )}

      {/* Statistiky */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-2xl font-bold text-orange-600">{stats.total}</div>
            <div className="text-sm text-gray-600">Celkem uživatelů</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-green-600">{stats.active}</div>
            <div className="text-sm text-gray-600">Aktivních v Raynet</div>
          </Card>
          <Card className="p-4">
            <div className="text-2xl font-bold text-purple-600">{stats.available}</div>
            <div className="text-sm text-gray-600">Dostupných pro formulář</div>
          </Card>
          <Card className="p-4">
            <div className="text-sm font-medium">Poslední synchronizace</div>
            <div className="text-xs text-gray-600">
              {stats.lastSync ? formatDate(stats.lastSync) : 'Nikdy'}
            </div>
          </Card>
        </div>
      )}

      <Separator />

      {/* Seznam uživatelů */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Seznam uživatelů Raynet</h3>
        
        {loading ? (
          <div className="text-center py-8">
            <p>Načítám uživatele...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-600">
              Žádní uživatelé nenalezeni. Spusťte synchronizaci s Raynet.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-4 py-2 px-4 bg-gray-50 rounded-md font-medium text-sm">
              <div className="col-span-2">Aktivní</div>
              <div className="col-span-2">Admin</div>
              <div className="col-span-4">Jméno</div>
              <div className="col-span-4">Uživatelské jméno</div>
            </div>
            
            {users.map((user) => (
              <div key={user.raynet_id} className="grid grid-cols-12 gap-4 py-3 px-4 border rounded-md hover:bg-gray-50">
                <div className="col-span-2 flex items-center">
                  <Checkbox
                    checked={user.is_available_for_forms}
                    onCheckedChange={(checked) => 
                      handleToggleAvailability(user.raynet_id, checked as boolean)
                    }
                  />
                </div>
                
                <div className="col-span-2 flex items-center">
                  <Checkbox
                    checked={user.is_admin}
                    onCheckedChange={(checked) => 
                      handleToggleAdmin(user.raynet_id, checked as boolean)
                    }
                  />
                </div>
                
                <div className="col-span-4 flex items-center">
                  <div>
                    <div className="font-medium">{user.full_name}</div>
                    <div className="text-xs text-gray-500">
                      Raynet ID: {user.raynet_id}
                      {user.person_id && ` • Person ID: ${user.person_id}`}
                    </div>
                  </div>
                </div>
                
                <div className="col-span-4 flex items-center">
                  <span className="font-mono text-sm">{user.username}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Nápověda */}
      <Card className="p-4 bg-orange-50 border-orange-200">
        <h4 className="font-semibold text-orange-800 mb-2">ℹ️ Jak to funguje</h4>
        <div className="text-sm text-orange-700 space-y-1">
          <p>• <strong>Synchronizace</strong> - stáhne aktuální seznam uživatelů z Raynet CRM</p>
          <p>• <strong>Aktivní checkbox</strong> - označte uživatele, kteří se mají objevit ve formuláři jako obchodníci</p>
          <p>• <strong>Admin checkbox</strong> - udělte uživateli práva správce aplikace (přístup do Admin rozhraní)</p>
          <p>• <strong>Neaktivní uživatelé</strong> - uživatelé deaktivovaní v Raynet nelze označit jako dostupné</p>
          <p>• <strong>Automatická synchronizace</strong> - doporučujeme spouštět pravidelně pro aktuální data</p>
        </div>
      </Card>
    </div>
  );
} 